import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  TemplateRef,
  HostListener,
} from '@angular/core';

import { fromEvent, merge, Subject, pipe } from 'rxjs';
import {
  map,
  takeUntil,
  throttleTime,
  debounceTime,
  distinctUntilChanged,
  startWith,
  skipWhile,
  share,
} from 'rxjs/operators';

import { stopPropagation } from '@fcom/utils';

import { TooltipData, TooltipOptions } from '../tooltip.interface';
import { TooltipPosition } from '../tooltip-position.enum';

const ABSOLUTE_X_PADDING = 10;

/**
 * The tooltip component is used for showing extra information to the user in a small popover,
 * there are a number of options you can use for positioning and opening conditions
 *
 * @example
 * <button
 *   fcomTooltip
 *   [options]="options"
 *   [content]="contentTooltip"
 *   [closeText]="closeText"
 *   [ariaCloseText]="ariaCloseText">
 *   Click
 * </button></div>
 */
@Component({
  selector: 'fcom-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TooltipComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngDestroyed$ = new Subject<void>();
  private readonly updatePosition$ = new Subject<Event>();
  private readonly resize$ = fromEvent(window, 'resize');
  private readonly scroll$ = fromEvent(window, 'scroll').pipe(debounceTime(30));
  private readonly arrowWidth = 14;

  private readonly arrowPositionPipe = pipe(
    map((triggerRect: DOMRect) => this.computeArrowPosition(triggerRect)),
    distinctUntilChanged()
  );
  private readonly tooltipPositionPipe = pipe(
    map(() => ({
      top: `${this.top || ''}px`,
      left: `${this.left}px`,
      width: `${this.tooltipWidth}px`,
    })),
    startWith({
      width: `${this.tooltipWidth}px`,
    }),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
  );
  private readonly triggerRectPipe = pipe(
    takeUntil(this.ngDestroyed$),
    skipWhile(() => !this.triggerRef),
    startWith(''),
    map(() => {
      this.height = this.tooltipContent.nativeElement.offsetHeight;
      return [this.triggerRef.nativeElement.getBoundingClientRect(), this.height];
      // recalculate if height or trigger Bounds change
    }),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    map(([triggerRect]: [DOMRect, number]) => {
      this.computeOffsetLeft(triggerRect);
      this.computeOffsetTop(triggerRect);
      this.changeDetectorRef.detectChanges();
      return triggerRect;
    }),
    share()
  );

  /**
   * The element that triggered the tooltip and will be used for positioning
   */
  @Input()
  triggerRef: ElementRef;

  /**
   * An object containing a number of options for the tooltip
   */
  @Input()
  tooltipOptions: TooltipOptions;

  /**
   * The content for the tooltip
   */
  @Input()
  tooltipData: TooltipData;

  /**
   * This event will trigger when the tooltip is closed
   */
  @Output()
  closeClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Used by the tooltip directive to control whether the tooltip should remain visible when hovering
   */
  @Output()
  mouseOnContent: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('closeButton')
  closeButton: ElementRef;

  @ViewChild('tooltipContent', { static: true })
  tooltipContent: ElementRef;

  @HostListener('document:click', ['$event'])
  clickout(event: Event): void {
    if (this.tooltipOptions.enableOnClick && !this.tooltipContent.nativeElement.contains(event.target)) {
      this.closeTooltip();
    }
  }

  top = 0;
  bottom = 0;
  left = 0;
  height = 0;

  arrowPositionClass: 'arrow-top' | 'arrow-bottom';

  triggerRect$ = merge(this.resize$, this.scroll$, this.updatePosition$).pipe(throttleTime(100), this.triggerRectPipe);
  tooltipPosition$ = this.triggerRect$.pipe(this.tooltipPositionPipe);
  arrowPosition$ = this.triggerRect$.pipe(this.arrowPositionPipe);

  private tooltipClosed = false;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  @HostListener('focusin')
  @HostListener('mouseenter')
  onmouseenter(): void {
    this.mouseOnContent.emit(true);
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  onmouseleave(): void {
    this.mouseOnContent.emit(false);
    if (this.tooltipOptions.enableOnHover) {
      this.closeTooltip();
    }
  }

  ngOnDestroy(): void {
    this.ngDestroyed$.next();
  }

  ngOnInit(): void {
    this.validInputs();
  }

  ngAfterViewInit(): void {
    this.validInputs();
    this.updatePosition$.next(null);

    this.changeDetectorRef.detectChanges();
  }

  validInputs(): never | boolean {
    if (!this.triggerRef) {
      throw new Error('Element reference is required');
    }
    return true;
  }

  private tooltipShouldBeOnTop(triggerRect: DOMRect): boolean {
    return this.height + this.arrowWidth < triggerRect.top;
  }

  private computeOffsetLeft(triggerRect: DOMRect): void {
    if (this.isAbsolutePosition) {
      this.left = -this.tooltipWidth;
    } else {
      if (triggerRect.right >= this.tooltipWidth) {
        this.left = triggerRect.right - this.tooltipWidth;
      } else {
        this.left = this.minOffsetLeft;
      }
    }
  }

  private computeOffsetTop(triggerRect: DOMRect): void {
    if (
      (this.tooltipPositionOpts === TooltipPosition.AUTOMATIC && !this.tooltipShouldBeOnTop(triggerRect)) ||
      this.tooltipPositionOpts === TooltipPosition.BOTTOM
    ) {
      this.arrowPositionClass = 'arrow-top';
      if (this.isAbsolutePosition) {
        // Need to trigger the change detection to have the latest value of the tooltip height
        this.changeDetectorRef.detectChanges();
        this.top = this.tooltipContent.nativeElement.getBoundingClientRect().height + 2 * this.arrowWidth;
      } else {
        this.top = triggerRect.bottom + this.arrowWidth;
      }
    } else {
      if (this.isAbsolutePosition) {
        this.arrowPositionClass = 'arrow-bottom';
        // Need to trigger the change detection to have the latest value of the tooltip height
        this.changeDetectorRef.detectChanges();
        this.top = -(this.tooltipContent.nativeElement.getBoundingClientRect().height + 2 * this.arrowWidth);
      } else {
        this.arrowPositionClass = 'arrow-bottom';
        this.top = triggerRect.top - (this.arrowWidth + this.height);
      }
    }
  }

  computeArrowPosition(triggerRect: DOMRect): number {
    if (this.isAbsolutePosition) {
      return this.tooltipContent.nativeElement.getBoundingClientRect().width - this.arrowWidth - ABSOLUTE_X_PADDING;
    }
    const internalOffset = triggerRect.left - this.left;
    return internalOffset + triggerRect.width / 2 - this.arrowWidth;
  }

  closeTooltip(event?: Event): void {
    stopPropagation(event);
    if (!this.tooltipClosed) {
      this.tooltipClosed = true;
      this.closeClick.emit(true);
    }
  }

  // Minimum offset left needed, in pixels
  get minOffsetLeft(): number {
    return this.tooltipOptions?.minOffsetLeft ?? ABSOLUTE_X_PADDING;
  }

  get tooltipWidth(): number {
    return this.tooltipOptions?.tooltipWidth ?? 342;
  }

  get tooltipPositionOpts(): TooltipPosition {
    return this.tooltipOptions?.tooltipPosition ?? TooltipPosition.AUTOMATIC;
  }

  get isAbsolutePosition(): boolean {
    return !!this.tooltipOptions?.useAbsolutePosition;
  }

  get content(): TemplateRef<ElementRef> {
    return this.tooltipData.content ?? null;
  }

  get closeText(): string {
    return this.tooltipData.closeText ?? '';
  }

  get ariaCloseText(): string {
    return this.tooltipData.ariaCloseText ?? 'close';
  }

  get showLeftBorder(): boolean {
    return this.tooltipOptions?.showLeftBorder ?? true;
  }

  get showArrowCaret(): boolean {
    return this.tooltipOptions?.showArrowCaret ?? true;
  }

  get roundedCorners(): boolean {
    return this.tooltipOptions?.roundedCorners ?? false;
  }

  get hasPadding(): boolean {
    return this.tooltipOptions?.hasPadding ?? true;
  }
}
